// Mảnh giao diện dùng chung cho xác thực 2 lớp (TOTP): ô nhập mã 6 số, khối quét mã QR, khối mã khôi phục.
// Dùng ở bước đăng nhập (LoginChallenge) và ở «Cài đặt cá nhân › Bảo mật» (MfaSettingsCard).
//
// Mã QR do MÁY CHỦ vẽ (SVG, thư viện `qrcode` ở API) — KHÔNG đưa link otpauth:// cho dịch vụ QR bên ngoài
// (api.qrserver.com…): link đó chứa secret, gửi ra ngoài là lộ lớp bảo vệ thứ hai.

import { useState } from 'react';
import toast from 'react-hot-toast';
import { Copy, Download, Loader2, KeyRound } from 'lucide-react';
import { Trans, useLingui } from '@lingui/react/macro';

/** Lỗi API → câu cho người dùng (BE đã đặt `message` tiếng của người dùng). */
export function apiErrorText(err, fallback) {
  return err?.response?.data?.message || err?.response?.data?.error?.message || fallback;
}

/** Ô nhập mã 6 số: chỉ nhận số, tự gửi khi đủ 6 (gõ hoặc dán). */
export function OtpCodeInput({ value, onChange, onComplete, disabled, autoFocus = true }) {
  const { t } = useLingui();
  return (
    <input
      type="text"
      inputMode="numeric"
      autoComplete="one-time-code"
      autoFocus={autoFocus}
      disabled={disabled}
      maxLength={6}
      value={value}
      onChange={(e) => {
        const v = e.target.value.replace(/\D/g, '').slice(0, 6);
        onChange(v);
        if (v.length === 6 && onComplete) onComplete(v);
      }}
      placeholder={t`Mã 6 số`}
      aria-label={t`Mã xác thực 6 số`}
      className="w-full text-center tracking-[0.5em] font-mono text-lg py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
    />
  );
}

/** Khối quét mã QR + secret gõ tay + ô nhập mã đầu tiên. `setup` = { qr_svg, secret, account }. */
export function MfaQrSetup({ setup, onSubmit, submitting, error }) {
  const { t } = useLingui();
  const [code, setCode] = useState('');
  const submit = (c) => { if (!submitting && c.length === 6) onSubmit(c); };
  const qrSrc = setup?.qr_svg ? `data:image/svg+xml;charset=utf-8,${encodeURIComponent(setup.qr_svg)}` : null;
  const prettySecret = (setup?.secret || '').replace(/(.{4})/g, '$1 ').trim();

  return (
    <div className="space-y-4">
      <ol className="text-sm text-slate-600 space-y-1 list-decimal pl-5">
        <li><Trans>Mở ứng dụng xác thực trên điện thoại (Google Authenticator, Microsoft Authenticator, Authy, 1Password…).</Trans></li>
        <li><Trans>Quét mã QR dưới đây, hoặc nhập khoá thiết lập bằng tay.</Trans></li>
        <li><Trans>Nhập mã 6 số ứng dụng hiện ra để xác nhận.</Trans></li>
      </ol>
      <div className="flex flex-col items-center gap-2">
        {qrSrc
          ? <img src={qrSrc} alt={t`Mã QR thiết lập xác thực 2 lớp`} className="w-44 h-44 border border-slate-200 rounded-lg bg-white p-2" />
          : <div className="w-44 h-44 border border-dashed border-slate-300 rounded-lg flex items-center justify-center text-xs text-slate-400 text-center px-3"><Trans>Không vẽ được mã QR — dùng khoá thiết lập bên dưới</Trans></div>}
        <div className="text-xs text-slate-500"><Trans>Khoá thiết lập</Trans></div>
        <div className="font-mono text-sm bg-slate-50 border border-slate-200 rounded px-2 py-1 select-all break-all text-center">{prettySecret}</div>
      </div>
      <form onSubmit={(e) => { e.preventDefault(); submit(code); }} className="space-y-3">
        <OtpCodeInput value={code} onChange={setCode} onComplete={submit} disabled={submitting} />
        {error && <p className="text-sm text-red-600">{error}</p>}
        <button
          type="submit"
          disabled={submitting || code.length !== 6}
          className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2"
        >
          {submitting && <Loader2 size={16} className="animate-spin" />}
          <Trans>Xác nhận</Trans>
        </button>
      </form>
    </div>
  );
}

/** 10 mã khôi phục — hiện MỘT lần (máy chủ chỉ giữ bản băm). */
export function RecoveryCodesPanel({ codes, onDone, doneLabel }) {
  const { t } = useLingui();
  const [saved, setSaved] = useState(false);
  const text = (codes || []).join('\n');
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); toast.success(t`Đã sao chép mã khôi phục`); setSaved(true); }
    catch { toast.error(t`Trình duyệt không cho sao chép — hãy tải tệp về`); }
  };
  const download = () => {
    try {
      const blob = new Blob([`${t`Mã khôi phục xác thực 2 lớp — mỗi mã dùng một lần`}\n\n${text}\n`], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'cnv-work-ma-khoi-phuc.txt';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setSaved(true);
    } catch { toast.error(t`Không tải được tệp — hãy sao chép`); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
        <KeyRound size={16} className="mt-0.5 shrink-0" />
        <span><Trans>Lưu các mã khôi phục này ở nơi an toàn. Mất điện thoại thì dùng một mã thay cho mã 6 số — mỗi mã chỉ dùng được một lần. Mã chỉ hiện lần này.</Trans></span>
      </div>
      <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-slate-50 border border-slate-200 rounded-lg p-3">
        {(codes || []).map((c) => <div key={c} className="text-center select-all">{c}</div>)}
      </div>
      <div className="flex gap-2">
        <button type="button" onClick={copy} className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 border border-slate-300 rounded-lg text-sm hover:bg-slate-50">
          <Copy size={14} /> <Trans>Sao chép</Trans>
        </button>
        <button type="button" onClick={download} className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 border border-slate-300 rounded-lg text-sm hover:bg-slate-50">
          <Download size={14} /> <Trans>Tải tệp .txt</Trans>
        </button>
      </div>
      {onDone && (
        <>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} className="rounded" />
            <Trans>Tôi đã lưu mã khôi phục</Trans>
          </label>
          <button
            type="button"
            disabled={!saved}
            onClick={onDone}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold py-2.5 rounded-lg"
          >
            {doneLabel || <Trans>Tiếp tục</Trans>}
          </button>
        </>
      )}
    </div>
  );
}
